import type { CSSProperties } from 'react';
import type { TemplateLogoAlignment, TemplateLogoSize } from '@/templates/domain/types/TemplateLogo';

/**
 * Max logo box sizes in CSS pixels (HTML preview).
 * PDF sizes MUST be derived via `pxToPt` (see `logoPdfImageStyle`) — do not reuse these
 * numbers as react-pdf points, or logos read larger relative to converted cover text.
 */
export const LOGO_SIZE_HEIGHT_PX: Record<TemplateLogoSize, number> = {
  small: 32,
  medium: 48,
  large: 72
};

export const LOGO_SIZE_MAX_WIDTH_PX: Record<TemplateLogoSize, number> = {
  small: 120,
  medium: 180,
  large: 240
};

export type LogoFlexAlign = 'flex-start' | 'center' | 'flex-end';

/** Maps template logoAlignment → Yoga/CSS flex alignment (row justifyContent or column alignItems). */
export function logoJustifyContent(alignment: TemplateLogoAlignment): LogoFlexAlign {
  if (alignment === 'center') return 'center';
  if (alignment === 'right') return 'flex-end';
  return 'flex-start';
}

/** Column-axis alias — prefer this for @react-pdf/renderer logo slots (see logoPdfSlotStyle). */
export function logoAlignItems(alignment: TemplateLogoAlignment): LogoFlexAlign {
  return logoJustifyContent(alignment);
}

/**
 * Maps template logoAlignment → react-pdf Image `objectPosition` (horizontal anchor within
 * a full-width contain/cover box). Uses the percentage form documented in react-pdf's Image
 * examples (`objectPosition: '50% 30%'`) — the keyword forms proved unreliable at 4.5.1.
 */
export function logoPdfObjectPosition(alignment: TemplateLogoAlignment): string {
  if (alignment === 'center') return '50% 50%';
  if (alignment === 'right') return '100% 50%';
  return '0% 50%';
}

export function logoSlotStyle(alignment: TemplateLogoAlignment, size: TemplateLogoSize): CSSProperties {
  return {
    display: 'flex',
    justifyContent: logoJustifyContent(alignment),
    alignItems: 'center',
    width: '100%',
    marginBottom: size === 'large' ? 12 : 8
  };
}

/** Preview logo box: maxHeight/maxWidth so short logos keep natural height (not upscaled). */
export function logoImageStyle(size: TemplateLogoSize): CSSProperties {
  return {
    maxHeight: LOGO_SIZE_HEIGHT_PX[size],
    maxWidth: LOGO_SIZE_MAX_WIDTH_PX[size],
    objectFit: 'contain',
    display: 'block'
  };
}

/**
 * PDF logo Image style — full-width box + `objectFit: contain` + `objectPosition`.
 *
 * After chasing Yoga flex layouts that all left the mark flush-left in @react-pdf/renderer 4.x,
 * the pattern that actually anchors the bitmap is the same one react-pdf uses for the page
 * background image: give the Image full parent width, `objectFit: contain` to preserve aspect,
 * and `objectPosition` to slide the scaled bitmap within the empty margin left by contain.
 * The parent slot's `justifyContent`/`alignItems` are no-ops here (the Image fills the row) but
 * remain for HTML preview parity via `logoPdfSlotStyle`.
 */
export function logoPdfImageStyle(
  size: TemplateLogoSize,
  pxToPt: (px: number) => number,
  alignment: TemplateLogoAlignment
): {
  width: '100%';
  height: number;
  objectFit: 'contain';
  objectPosition: string;
} {
  return {
    width: '100%',
    height: pxToPt(LOGO_SIZE_HEIGHT_PX[size]),
    objectFit: 'contain',
    objectPosition: logoPdfObjectPosition(alignment)
  };
}

/**
 * Full-width PDF logo slot — row-flex + `justifyContent`.
 *
 * A row-flex parent lets `justifyContent` position an intrinsic-width child on the main axis
 * (horizontal). Paired with an Image that has NO explicit `width` (see `logoPdfImageStyle`),
 * Yoga renders the Image at its intrinsic aspect ratio and this parent then anchors it. Column
 * layouts and `alignSelf`/`alignItems` on the Image were all defeated by react-pdf's stretch
 * default — this row-flex pattern is the one that actually moves the mark.
 */
export function logoPdfSlotStyle(
  alignment: TemplateLogoAlignment,
  extras?: { marginBottom?: number; marginTop?: number }
): {
  width: '100%';
  flexDirection: 'row';
  justifyContent: LogoFlexAlign;
  alignItems: 'center';
  marginBottom?: number;
  marginTop?: number;
} {
  return {
    width: '100%',
    flexDirection: 'row',
    justifyContent: logoJustifyContent(alignment),
    alignItems: 'center',
    ...(extras?.marginBottom != null ? { marginBottom: extras.marginBottom } : {}),
    ...(extras?.marginTop != null ? { marginTop: extras.marginTop } : {})
  };
}
